import * as React from 'react';
import { Col } from 'react-bootstrap';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import type { ResolveThunks } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';

import type { W_Company_EditInvoiceNotificationsProperties_RM } from 'ab-api/web/company/editInvoiceNotifications';

import { ALLOWED_IMAGE_TYPES, ALLOWED_IMAGE_TYPES_STR } from '@acceligentllc/shared/enums/fileType';

import CompanyFeatureFlagsEnum, { CompanyFeatureFlagsTips } from 'ab-enums/companyFeatureFlags.enum';
import { InvoiceSenderEmailDomains } from 'ab-enums/invoiceSenderEmailDomains.enum';

import { Button } from '@acceligentllc/storybook';

import Checkbox from 'af-components/Controls/Checkbox';
import Dropdown from 'af-components/Controls/Dropdown';
import CustomModal from 'af-components/CustomModal';
import Textarea from 'af-components/Fields/PlainTextArea';
import ImageTag from 'af-components/Image';
import LockedValue from 'af-components/LockedValue';
import SubmitButton from 'af-components/SubmitButton';

import * as CompanyActions from 'af-actions/companies';

import PreviewItem from '../../Shared/PreviewItem';

type OwnProps = {
	closeModal: () => void;
	isPlatformAdmin: boolean;
	initialIsSendingInvoiceNotificationsEnabled: boolean;
	initialImageUrl: Nullable<string>;
	initialMessage: string;
	initialEmailDomain: Nullable<string>;
};

interface DispatchProps {
	editEnableInvoiceNotifications: typeof CompanyActions.editEnableInvoiceNotifications;
}

type FormOwnProps = OwnProps & ResolveThunks<DispatchProps>;

type FormProps = InjectedFormProps<W_Company_EditInvoiceNotificationsProperties_RM, FormOwnProps>;

type Props = FormOwnProps & ConnectedProps<typeof connector> & FormProps;

interface EmailDomainOption {
	id: InvoiceSenderEmailDomains;
	name: InvoiceSenderEmailDomains;
}

const EnableInvoiceNotificationsCompanyEdit: React.FC<Props> = (props) => {
	const {
		closeModal,
		editEnableInvoiceNotifications,
		isPlatformAdmin,
		initialEmailDomain,
		initialImageUrl,
		initialIsSendingInvoiceNotificationsEnabled,
		initialMessage,
	} = props;

	const [enableInvoiceNotifications, setEnableInvoiceNotifications] = React.useState(initialIsSendingInvoiceNotificationsEnabled ?? false);
	const [image, setImage] = React.useState<Nullable<string | File>>(initialImageUrl ?? null);
	const [imageUrl, setImageUrl] = React.useState<Nullable<string>>(initialImageUrl ?? null);
	const [invoiceMessage, setInvoiceMessage] = React.useState<string>(initialMessage ?? '');
	const [emailDomain, setEmailDomain] = React.useState<Nullable<EmailDomainOption>>(
		initialEmailDomain
			? { id: initialEmailDomain, name: initialEmailDomain } as EmailDomainOption
			: null);
	const [deleteImage, setDeleteImage] = React.useState(false);
	const [uploadError, setUploadError] = React.useState<Nullable<string>>(null);
	const inputElementRef = React.useRef<HTMLInputElement>(null);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const emailDomainValues: EmailDomainOption[] = React.useMemo(() => {
		return Object.values(InvoiceSenderEmailDomains).map((domain) => { return { id: domain, name: domain }; });
	}, []);

	const renderLabel = React.useCallback(() => {
		return (
			<PreviewItem
				checked={!!enableInvoiceNotifications}
				hint={CompanyFeatureFlagsTips.invoiceNotifications}
				indent={false}
				label={CompanyFeatureFlagsEnum.isSendingInvoiceNotificationsEnabled}
				showCheckbox={false}
			/>
		);
	}, [enableInvoiceNotifications]);

	const handleCheckboxChanged = React.useCallback((event) => {
		setEnableInvoiceNotifications(event.target.checked);
	}, []);

	const handleMessageChanged = React.useCallback((value) => {
		setInvoiceMessage(value);
	}, []);

	const handleEmailDomainChanged = React.useCallback((value) => {
		setEmailDomain(value);
	}, []);

	const onClearEmailDomain = React.useCallback(() => {
		setEmailDomain(null);
	}, []);

	const renderEmailDomainItem = React.useCallback((optionItem: EmailDomainOption) => {
		if (optionItem === null) {
			return <></>;
		}
		return <>{optionItem.name}</>;
	}, []);

	const onInputClick = React.useCallback(() => {
		setUploadError(null);
		if (inputElementRef.current) {
			inputElementRef.current.click();
		}
	}, []);

	const changeImage = React.useCallback((event) => {
		const [file] = event.target.files as File[];

		if (file) {
			if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
				const reader = new FileReader();
				reader.onloadend = () => {
					const _result = reader.result as string;
					setImage(file);
					setImageUrl(_result);
					setDeleteImage(true);

				};
				reader.readAsDataURL(file);
			} else {
				setUploadError(`Invalid file format. Only ${ALLOWED_IMAGE_TYPES_STR} allowed.`);
			}
		}
	}, []);

	const onDeleteImage = React.useCallback(() => {
		setImage(null);
		setImageUrl(null);
		setDeleteImage(true);
		if (inputElementRef.current?.value) {
			inputElementRef.current.value = '';
		}
	}, [inputElementRef]);

	const onSubmit = React.useCallback(async () => {
		setIsSubmitting(true);
		await editEnableInvoiceNotifications({
			emailDomain: !!emailDomain?.name ? emailDomain?.name : null,
			isSendingInvoiceNotificationsEnabled: enableInvoiceNotifications,
			image,
			imageUrl,
			deleteImage,
			invoiceMessage,
		});
		closeModal();
	}, [emailDomain, editEnableInvoiceNotifications, enableInvoiceNotifications, image, imageUrl, deleteImage, invoiceMessage, closeModal]);

	const onClose = React.useCallback(() => {
		closeModal();
	}, [closeModal]);

	return (
		<CustomModal
			closeModal={onClose}
			modalStyle="info"
			showModal={true}
			size="lg"
		>
			<CustomModal.Header
				closeModal={onClose}
				title="Edit Invoice Notifications"
			/>
			<CustomModal.Body>
				<Checkbox
					handleChange={handleCheckboxChanged}
					isChecked={enableInvoiceNotifications}
					label={renderLabel()}
				/>
				{
					isPlatformAdmin && enableInvoiceNotifications &&
					<>
						<br />
						<Dropdown
							defaultValue={emailDomain}
							hasBlankOption={true}
							id="emailDomain-dropdown"
							isWhite={true}
							label="Email Domain"
							labelKey="name"
							onClear={onClearEmailDomain}
							onValueChange={handleEmailDomainChanged}
							options={emailDomainValues}
							placeholder="Email Domain"
							renderMenuItem={renderEmailDomainItem}
							renderSelected={renderEmailDomainItem}
							tooltipMessage={'Optionally enter company\'s email domain to whitelist it. When it is whitelisted here, you can use it in invoice notification settings, in "From" part.'}
							valueKey="name"
							withCaret={true}
						/>
						<br />
						<Textarea
							label="Invoice Message"
							maxCharacters={5000}
							onValueChange={handleMessageChanged}
							placeholder="Invoice Message"
							rows={10}
							showMaxCharactersLabel={true}
							value={invoiceMessage}
						/>
						<br />
						<>
							<LockedValue
								label="Invoice Header Image"
								renderValue={false}
								value={imageUrl}
							/>
							<Col sm={24}>
								<ImageTag
									className="company-settings__printout"
									src={imageUrl}
								/>
							</Col>
							<Col sm={24}>
								Suggested image size is 353 × 100.
							</Col>
							<Col className="image-upload__buttons" sm={21}>
								<Button
									customStyleClass="image-upload__button"
									icon="ActionImport"
									onClick={onInputClick}
									style="secondary"
									tooltip="Upload"
								/>
								<Button
									disabled={!image}
									icon="Delete"
									onClick={onDeleteImage}
									style="danger"
									tooltip="Delete"
								/>
								<input accept={ALLOWED_IMAGE_TYPES} className="display-none" onChange={changeImage} ref={inputElementRef} type="file" />
							</Col>
							{uploadError &&
								<Col sm={24}>
									<span className="help-block">{uploadError}</span>
								</Col>
							}
						</>
					</>
				}
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={closeModal}
					style="secondary"
				/>
				<SubmitButton
					label="Save"
					onClick={onSubmit}
					reduxFormSubmitting={isSubmitting}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapDispatchToProps() {
	return {
		editEnableInvoiceNotifications: CompanyActions.editEnableInvoiceNotifications,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(connector);

export default enhance(EnableInvoiceNotificationsCompanyEdit);
